import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/i18n/i18n';

/**
 * LanguageSwitcher Component
 * A dropdown to switch site language. Uses Framer Motion for animations.
 * No Radix UI - built with native HTML elements.
 */
export default function LanguageSwitcher({ variant = 'default', className = '' }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get current language details
  const currentLang = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language) 
    || SUPPORTED_LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  // Compact variant for mobile
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-white/10 rounded-sm transition-colors"
          aria-label={`Current language: ${currentLang.name}. Click to change language.`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-testid="language-switcher-compact"
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          <span className="uppercase font-medium">{currentLang.code}</span>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-40 py-1 bg-[#0C0C0C] border border-zinc-800 rounded-sm shadow-xl z-50"
              role="listbox"
              aria-label="Select language"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors ${
                    i18n.language === lang.code
                      ? 'text-[#D4AF37] bg-[#D4AF37]/10'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                  role="option"
                  aria-selected={i18n.language === lang.code}
                  data-testid={`language-option-${lang.code}`}
                >
                  <span className="text-base" aria-hidden="true">{lang.flag}</span>
                  <span className="flex-1">{lang.name}</span>
                  {i18n.language === lang.code && (
                    <Check className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default variant - shows flag and full name
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-white/10 rounded-sm transition-colors border border-transparent hover:border-zinc-700"
        aria-label={`Current language: ${currentLang.name}. Click to change language.`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-testid="language-switcher"
      >
        <span className="text-lg" aria-hidden="true">{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.name}</span>
        <ChevronDown 
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          aria-hidden="true" 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for mobile */}
            <div 
              className="fixed inset-0 z-40 md:hidden" 
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-48 py-1 bg-[#0C0C0C] border border-zinc-800 rounded-sm shadow-xl z-50"
              role="listbox"
              aria-label="Select language"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 text-left text-sm transition-colors ${
                    i18n.language === lang.code
                      ? 'text-[#D4AF37] bg-[#D4AF37]/10'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                  role="option"
                  aria-selected={i18n.language === lang.code}
                  data-testid={`language-option-${lang.code}`}
                >
                  <span className="text-lg" aria-hidden="true">{lang.flag}</span>
                  <span className="flex-1">{lang.name}</span>
                  {i18n.language === lang.code && (
                    <Check className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
                  )}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
